/**
 * ActivationCard — editorial "Business Setup" arc that replaces the
 * old GettingStartedGuide. Shows a progress ring, a headline that
 * shifts with completion, and a checklist of nine steps. Hides
 * itself once fully activated, and supports a soft per-progress
 * dismiss so studios can hide it but still see the next milestone.
 *
 * POL-4 (Item 1) — Restyled to match the WelcomeSequence editorial
 * dark aesthetic: solid dark surface with an orbit-orange soft
 * halo, Fraunces italic headline, uppercase orbit-eyebrow label,
 * subtle row hover, and a primary orbit-orange CTA on the active
 * step. The "Studio setup" eyebrow is now "Business setup" per
 * the Studio → Business rebrand.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, ArrowRight, ChevronUp } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { cn } from '@/lib/utils';
import { useActivationProgress } from '@/hooks/useActivationProgress';
import { useActivationCelebrations } from '@/hooks/useActivationCelebrations';
import { Nova } from '@/components/nova';

const DISMISS_KEY = 'orbit:activation:dismissed-until-progress';

function ProgressRing({ fraction, size = 64, stroke = 5, label }: { fraction: number; size?: number; stroke?: number; label?: string }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - Math.max(0, Math.min(1, fraction)));
  const pct = Math.round(fraction * 100);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.16)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="hsl(var(--orbit-orange))"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 600ms cubic-bezier(0.22, 1, 0.36, 1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-[13px] font-semibold tabular-nums text-white">
        {label ?? `${pct}%`}
      </div>
    </div>
  );
}

export function ActivationCard({ collapsible = false, forceShow = false }: { collapsible?: boolean; /** DEVMODE-1 — ignore activation/dismiss/collapse so the card can be previewed. */ forceShow?: boolean }) {
  const { steps, completedCount, totalCount, fraction, isFullyActivated, nextStep } =
    useActivationProgress();
  useActivationCelebrations(steps);

  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  // UX-7 — collapse state for the compact pill treatment (below).
  // Session-local on purpose: expanding is a peek, not a preference,
  // so the card folds back down on the next visit.
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      setDismissedAt(raw === null ? null : Number(raw));
    } catch {
      setDismissedAt(null);
    }
  }, []);

  if (isFullyActivated && !forceShow) return null;

  // Soft dismiss: only honor the dismissal until the user makes more progress.
  if (!forceShow && dismissedAt !== null && completedCount <= dismissedAt) return null;

  // UX-7 — once the studio has real momentum (≥3 steps done), the
  // full nine-row checklist stops earning its height on Today. When
  // the parent opts in via `collapsible` (NewDashboard does; the
  // legacy Index.tsx doesn't, so it's unchanged), the card renders as
  // a compact "Setup 4/9" pill that expands on click. This is purely
  // a presentation fold: completion logic, ordering (ONB-3), soft
  // dismiss and celebrations above all run exactly as before.
  const canCollapse = collapsible && completedCount >= 3 && !forceShow;
  if (canCollapse && !expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-expanded={false}
        aria-label={`Business setup — ${completedCount} of ${totalCount} steps complete. Expand checklist.`}
        className="group inline-flex items-center gap-3 rounded-full border border-white/10 bg-neutral-950 px-4 py-2.5 shadow-[0_10px_30px_-18px_rgba(0,0,0,0.55)] hover:border-white/20 transition-colors"
      >
        <span
          aria-hidden
          className="inline-block w-2 h-2 rounded-full bg-orbit-orange shadow-[0_0_10px_-1px_rgba(226,101,54,0.7)]"
        />
        <span className="text-sm font-medium text-white/85 group-hover:text-white tabular-nums">
          Setup {completedCount}/{totalCount}
        </span>
        <OrbitIcon name="arrow.right" fallback={ArrowRight} className="w-3.5 h-3.5 text-white/50 group-hover:text-white/80" />
      </button>
    );
  }

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(completedCount));
    } catch {
      /* ignore */
    }
    setDismissedAt(completedCount);
  };

  // SIGNUP-5 — copy follows the prototype's "Three things to start
  // with" card: a plain-spoken headline, one line of reassurance,
  // then the list. The nine steps stay (they're the studio's map).
  const headline =
    completedCount === 0
      ? 'A few things to start with'
      : fraction < 0.5
        ? 'A few quick wins'
        : 'Almost there';

  const sub =
    completedCount === 0
      ? 'Most studios finish these in their first week.'
      : nextStep
        ? `Next up: ${nextStep.label}`
        : '';

  return (
    <section
      // SIGNUP-5 — restyled to the prototype's frosted glass panel:
      // translucent white surface, inset hairline, a soft lilac
      // bloom, and the 60px ring counting steps. Sits on the Today
      // sky without a solid black slab.
      className="relative overflow-hidden rounded-[32px] p-5 md:p-7 text-white"
      style={{
        background: 'rgba(18,14,26,0.58)',
        backdropFilter: 'blur(28px) saturate(140%)',
        WebkitBackdropFilter: 'blur(28px) saturate(140%)',
        boxShadow:
          'inset 0 1px 0 rgba(255,255,255,0.2), inset 0 0 0 1px rgba(255,255,255,0.12), 0 0 60px rgba(214,150,255,0.14)',
      }}
      aria-label="Business setup activation"
    >
      {/* UX-7 — collapse back to the pill. Only offered when the pill
          treatment is active for this mount; sits left of the X. */}
      {canCollapse && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="absolute top-4 right-12 p-1.5 rounded-full text-white/40 hover:text-white/80 hover:bg-white/[0.06] transition-colors z-10"
          aria-label="Collapse setup card"
        >
          <OrbitIcon name="chevron.up" fallback={ChevronUp} className="w-4 h-4" />
        </button>
      )}

      <button
        type="button"
        onClick={handleDismiss}
        className="absolute top-4 right-4 p-1.5 rounded-full text-white/40 hover:text-white/80 hover:bg-white/[0.06] transition-colors z-10"
        aria-label="Dismiss setup card"
      >
        <OrbitIcon name="xmark" fallback={X} className="w-4 h-4" />
      </button>

      <header className="relative flex items-center gap-4 pb-3.5 pr-16">
        <ProgressRing fraction={fraction} size={60} stroke={5} label={`${completedCount}/${totalCount}`} />
        <div className="min-w-0 flex-1">
          <div className="text-[18px] md:text-[22px] font-bold tracking-[-0.015em] leading-tight">
            {headline}
          </div>
          {sub && <p className="text-sm text-white/70 mt-0.5">{sub}</p>}
        </div>
        <div className="hidden sm:block shrink-0 -my-1">
          <Nova state="has-suggestion" size={44} />
        </div>
      </header>

      <ul className="relative">
        {steps.map((step, i) => {
          const isNext = !step.completed && nextStep?.id === step.id;
          return (
            <li
              key={step.id}
              className="flex flex-wrap items-center gap-x-3.5 gap-y-2 py-3.5"
              style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1)' }}
            >
              <span
                className={cn(
                  'grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full text-[13px] font-semibold',
                  step.completed ? 'bg-orbit-orange text-[#1A1422]' : 'text-white/80',
                )}
                style={step.completed ? undefined : { boxShadow: 'inset 0 0 0 1.5px rgba(255,255,255,0.3)' }}
              >
                {step.completed ? <OrbitIcon name="checkmark" fallback={Check} className="w-3.5 h-3.5" strokeWidth={3} /> : i + 1}
              </span>
              <div className="min-w-0" style={{ flex: '1 1 180px' }}>
                <div
                  className={cn(
                    'text-[15px] md:text-base font-semibold',
                    step.completed ? 'text-white/60 line-through' : 'text-white',
                  )}
                >
                  {step.label}
                </div>
                {!step.completed && (
                  <p className="text-sm text-white/70 mt-0.5 leading-snug">{step.hint}</p>
                )}
              </div>
              {step.completed ? (
                <span className="text-sm font-medium text-orbit-orange">Done</span>
              ) : isNext ? (
                <Link
                  to={step.href}
                  className="inline-flex h-12 w-full sm:w-auto items-center justify-center whitespace-nowrap rounded-full px-7 text-[15px] font-semibold text-white transition-transform hover:scale-[1.02]"
                  style={{ background: '#2F6BE8', boxShadow: '0 10px 30px rgba(47,107,232,0.45)' }}
                >
                  {step.ctaLabel}
                </Link>
              ) : (
                <Link
                  to={step.href}
                  className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-full px-[18px] text-sm font-medium text-white/90 transition-colors hover:bg-white/20"
                  style={{ background: 'rgba(255,255,255,0.12)' }}
                >
                  Start
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
